import { CssBaseline } from '@mui/material';
import { Font } from '@react-pdf/renderer';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { StylesProvider } from '@mui/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { FC, Suspense } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Router } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { SWRConfig } from 'swr';
import { QueryClientProvider } from '@tanstack/react-query';
import 'react-toastify/dist/ReactToastify.css';
import { ModalProvider } from 'store/modal';
import { JobsProvider } from 'store/jobs';
import { Loader } from 'melp-design/components';
// keep this to avoid circular dependency
import { queryClient } from 'utils/query-client/query-client';
import { routerHistory } from 'utils/router-history';
import Hotjar from '@hotjar/browser';
import * as Sentry from '@sentry/react';
import { APP } from 'config';
import { SWROptions } from 'api/config';
import { AuthProvider } from 'auth/index';
import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import { Base } from 'components/layouts/Base/Base';
import i18n from 'i18n';
import { MeProvider } from 'state/Administrators';
import { ParentCompanyProvider } from 'state/ParentCompany';
import Melp from 'styles/themes/Melp';
import ScrollToTop from 'utils/ScrollToTop';
import { FeatureFlagsListener } from 'containers/feature-flags';
import { SessionInactivityListener } from 'containers/session';
import Routes from '../../Routes/Routes';
import pdfFont from './assets/Inter-Regular.ttf';
import pdfFontBold from './assets/Inter-SemiBold.ttf';

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: APP.sentry.dsn,
    environment: APP.env,
  });
}
Hotjar.init(APP.hotjar.siteId, APP.hotjar.version);

Font.register({
  family: 'Inter',
  fonts: [{ src: pdfFont }, { src: pdfFontBold, fontStyle: 'bold' }],
});

export const App: FC = () => {
  return (
    <ErrorBoundary>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={Melp} key="melp">
          <StylesProvider injectFirst>
            <QueryClientProvider client={queryClient}>
              <JobsProvider>
                <I18nextProvider i18n={i18n}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <SWRConfig value={SWROptions}>
                      <Suspense fallback={<Loader />}>
                        <CssBaseline key="melp" />
                        <Router history={routerHistory}>
                          <ScrollToTop />
                          <AuthProvider>
                            <MeProvider>
                              <ParentCompanyProvider>
                                <ModalProvider>
                                  <FeatureFlagsListener />
                                  <SessionInactivityListener />
                                  <Base>
                                    <Routes />
                                  </Base>
                                </ModalProvider>
                              </ParentCompanyProvider>
                            </MeProvider>
                          </AuthProvider>
                        </Router>
                        <ToastContainer
                          position="top-right"
                          autoClose={5000}
                          hideProgressBar={true}
                          newestOnTop
                          rtl={false}
                          closeOnClick={false}
                          draggable={false}
                          pauseOnHover
                        />
                      </Suspense>
                    </SWRConfig>
                  </LocalizationProvider>
                </I18nextProvider>
              </JobsProvider>
            </QueryClientProvider>
          </StylesProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </ErrorBoundary>
  );
};
